<template>
  <div>
    <div class="d-flex flex-wrap mb-5">
      <div class="info-wrap">
        <div class="info-wrap-status">
          <div class="info-wrap-status-box success"></div>
          <div class="info-wrap-status-txt">
            {{ $t("stillageStatus.empty") }}
          </div>
        </div>
      </div>
      <div class="info-wrap">
        <div class="info-wrap-status">
          <div class="info-wrap-status-box warning"></div>
          <div class="info-wrap-status-txt">
            {{ $t("stillageStatus.hasSpace") }}
          </div>
        </div>
      </div>
      <div class="info-wrap">
        <div class="info-wrap-status">
          <div class="info-wrap-status-box error"></div>
          <div class="info-wrap-status-txt">
            {{ $t("stillageStatus.full") }}
          </div>
        </div>
      </div>
    </div>

    <div class="warehouse-reference-wrapper">
      <div class="d-flex">
        <div
          ref="wrapper"
          :style="{
            overflow: 'auto',
            width: 'auto',
            maxWidth: '100%',
            maxHeight: $vuetify.breakpoint.height - 260 + 'px',
          }"
        >
          <!-- +=+=+=+=++=+=+=+=+ |column numeration| +=+=+=+=++=+=+=+=+ -->
          <div
            class="warehouse-view-table-navigation warehouse-view-table-navigation-horizontal"
          >
            <WarehouseBox
              :width="columnWidth"
              :key="`letter-${letterIndex}`"
              :class="['warehouse-index-top-box']"
              class="text-uppercase"
              v-for="(columnWidth, letterIndex) in data.columns"
              >{{ getLetter(letterIndex) }}
            </WarehouseBox>
          </div>
          <!--column numeration-->

          <div class="d-flex">
            <!-- +=+=+=+=++=+=+=+=+ |row numeration| +=+=+=+=++=+=+=+=+ -->
            <div
              class="warehouse-view-table-navigation warehouse-view-table-navigation-vertical"
            >
              <WarehouseBox
                :key="`column-${columnIndex}`"
                :class="['warehouse-index-left-box']"
                :height="item"
                v-for="(item, columnIndex) in data.rows"
                >{{ columnIndex + 1 }}
              </WarehouseBox>
            </div>
            <!--row numeration-->

            <div class="d-flex text-uppercase table-cell-wrapper">
              <!-- +=+=+=+=++=+=+=+=+ |columns start| +=+=+=+=++=+=+=+=+ -->
              <div
                v-for="(column, columnIndex) in data.boards"
                :key="columnIndex"
              >
                <WarehouseStillageBox
                  :can-resize="!!row.id"
                  :width="row.width"
                  :height="row.height"
                  @changeWidth="
                    changeBoxSize({
                      type: 'x',
                      rowIndex,
                      columnIndex,
                      value: $event,
                      id: row.id,
                    })
                  "
                  @changeHeight="
                    changeBoxSize({
                      type: 'y',
                      rowIndex,
                      columnIndex,
                      value: $event,
                      id: row.id,
                    })
                  "
                  @click="
                    boxClickHandler({
                      event: $event,
                      columnIndex,
                      rowIndex,
                      id: row.id,
                    })
                  "
                  :key="`row-${rowIndex}-${columnIndex}`"
                  v-for="(row, rowIndex) in column"
                  :item="row"
                >
                  {{ getLetter(columnIndex) }}{{ rowIndex + 1 }}
                </WarehouseStillageBox>
                <WarehouseBox
                  :width="data['columns'][columnIndex]"
                  @click="
                    addBoxClickHandler({
                      type: BOX_POSITION.row,
                      columnIndex,
                      event: $event,
                    })
                  "
                  class="warehouse-view-table-box"
                >
                  <img src="@/assets/img/box-plus.svg" alt="" />
                </WarehouseBox>
              </div>
              <!-- columns end-->

              <div class="d-flex">
                <WarehouseBox
                  :height="data.rows[0]"
                  @click="
                    addBoxClickHandler({
                      type: BOX_POSITION.col,
                      event: $event,
                    })
                  "
                  class="warehouse-view-table-box"
                >
                  <img src="@/assets/img/box-plus.svg" alt="" />
                </WarehouseBox>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ContextMenu
      @closed="clear"
      v-model="currentItem.isVisibleContextEditMenu"
      :position="currentItem.contextMenuPosition"
    >
      <VBtn @click="deleteBoxConfirmHandler" width="100%" color="error"
        >{{ $t("delete") }}
      </VBtn>
    </ContextMenu>
    <ContextMenu
      :position="currentItem.contextMenuPosition"
      v-model="currentItem.isVisibleCreateContextMenu"
      @closed="clear"
    >
      <CustomInputText
        ref="rowInput"
        :label="$t('row')"
        @keydown.enter="addBoxHandler"
        v-model="currentItem.rowCount"
        @keypress="numberOnly"
        style="width: 100%"
        class="user_input no-margin"
      />
      <template #footer>
        <div class="d-flex justify-space-between mt-2 mx-2 my-2">
          <v-btn width="45%" color="#DFE2E9" @click="clear">
            <v-icon color="#7A889B">mdi-close-circle</v-icon>
          </v-btn>
          <v-btn
            width="45%"
            class="ml-2"
            color="primary"
            @click="addBoxHandler"
          >
            <v-icon>mdi-check-circle</v-icon>
          </v-btn>
        </div>
      </template>
    </ContextMenu>
    <WarningDialog
      @apply="deleteBoxHandler"
      v-model="currentItem.warningDialog"
    ></WarningDialog>
    <NotificationSystem ref="notificationError" />
  </div>
</template>

<script>
import { getLetter } from "@/utils/numeration";
import "./warehouse.css";
import "./warehouse-edit-table.css";
import ContextMenu from "@/components/ui/context-menu/ContextMenu";
import WarningDialog from "@/components/ui/prompt/warning-dialog";
import { trueSet } from "@/utils/pureFunctions";
import WarehouseStillageBox from "@/components/warehouse/StillageBox";
import { numberOnly } from "@/utils/inputRegExps";
import WarehouseBox from "@/components/warehouse/WarehouseBox.vue";
import { DEFAULT_STILLAGE_BOX_SIZE } from "@/utils/constants.js";
import NotificationSystem from "@/components/notificationSystem.vue";

const BOX_POSITION = {
  col: "col",
  row: "row",
};

const DEFAULT_CURRENT_ITEM = {
  warningDialog: false,
  type: null,
  rowCount: null,
  columnIndex: null,
  id: null,
  rowIndex: null,
  positionType: BOX_POSITION.col,
  isVisibleContextEditMenu: false,
  isVisibleCreateContextMenu: false,
  contextMenuPosition: {
    top: 100,
    left: 100,
  },
};

export default {
  name: "warehouse-stillage",
  data() {
    return {
      currentItem: { ...DEFAULT_CURRENT_ITEM },
    };
  },
  components: {
    NotificationSystem,
    WarehouseBox,
    WarehouseStillageBox,
    WarningDialog,
    ContextMenu,
  },
  props: {
    data: { type: Object },
  },
  methods: {
    async deleteBoxHandler() {
      const { rowIndex, columnIndex, id } = this.currentItem;
      if (id) {
        this.showLoading();
        try {
          await this.$http.delete("stillage/delete-cell", {
            params: { id },
          });
        } catch (e) {
          // this.setResponseError({ error: e });
          this.$refs.notificationError.open({
            title: e.response.data.error_message.title,
          });
          throw new Error(e);
        } finally {
          this.clearLoading();
        }
      }
      if (this.data.boards[columnIndex].length === 1) {
        this.data.boards.splice(columnIndex, 1);
      } else {
        this.data.boards[columnIndex].splice(rowIndex, 1);
      }
      this.clear();
    },
    deleteBoxConfirmHandler() {
      const { id } = this.currentItem;
      this.currentItem = {
        ...this.currentItem,
        warningDialog: !!id,
        isVisibleContextEditMenu: false,
      };
      if (!id) {
        this.deleteBoxHandler();
      }
    },
    boxClickHandler({ event, columnIndex, rowIndex, id }) {
      const { clientX: left, clientY: top } = event;
      this.currentItem = {
        ...this.currentItem,
        contextMenuPosition: { top, left },
        isVisibleContextEditMenu: true,
        columnIndex,
        rowIndex,
        id: id || null,
      };
    },
    createRow({ rowCount, columnIndex }) {
      const rows = [];
      let offsetCount = 0;
      if (columnIndex !== null)
        offsetCount = this.data.boards[columnIndex].length;
      for (let i = 0; i < rowCount; i++) {
        let width = DEFAULT_STILLAGE_BOX_SIZE;
        let height = DEFAULT_STILLAGE_BOX_SIZE;
        if (this.data.rows[offsetCount + i])
          height = this.data.rows[offsetCount + i];
        if (columnIndex !== null) width = this.data.columns[columnIndex];
        rows[i] = {
          hasProduct: "false",
          full: "false",
          width,
          height,
        };
      }
      return rows;
    },
    addBoxClickHandler({ type, event, columnIndex }) {
      const { clientX: left, clientY: top } = event;
      this.currentItem = {
        ...this.currentItem,
        contextMenuPosition: { left, top },
        isVisibleCreateContextMenu: true,
        positionType: type,
        columnIndex,
      };
      this.$nextTick(() => {
        const { rowInput } = this.$refs;
        if (rowInput) rowInput.focus();
      });
    },
    addBoxHandler() {
      let { positionType, rowCount, columnIndex } = this.currentItem;
      if (rowCount) rowCount = +rowCount;
      if (!rowCount || rowCount === 0) rowCount = 1;
      if (rowCount > 15) rowCount = 15;
      if (positionType === BOX_POSITION.col) {
        this.data.columns.push(DEFAULT_STILLAGE_BOX_SIZE);
        const difference = rowCount - this.data.rows.length;
        if (difference > 0) {
          this.data.rows.push(
            ...new Array(difference).fill(DEFAULT_STILLAGE_BOX_SIZE)
          );
        }
        const rows = this.createRow({ rowCount, columnIndex: null });
        this.data.boards.push(rows);
        this.clear();
        return;
      }
      if (positionType === BOX_POSITION.row) {
        let difference =
          rowCount +
          this.data.boards[columnIndex].length -
          this.data.rows.length;
        if (difference < 0) difference = 0;
        this.data.rows = [
          ...this.data.rows,
          ...new Array(difference).fill(DEFAULT_STILLAGE_BOX_SIZE),
        ];
        const initialRows = this.data.boards[columnIndex];
        const rows = this.createRow({
          rowCount,
          columnIndex,
          rowIndex: initialRows.length,
        });
        this.data.boards[columnIndex] = [...initialRows, ...rows];
        this.clear();
      }
    },
    save() {
      this.$emit("save");
    },
    clear() {
      this.currentItem = { ...DEFAULT_CURRENT_ITEM };
    },
    changeBoxSize({ type, rowIndex, columnIndex, value }) {
      const ids = [];
      if (type === "x") {
        this.$set(
          this.data.boards,
          columnIndex,
          this.data.boards[columnIndex].map((item) => {
            item.width = value;
            ids.push(item.id);
            return item;
          })
        );
        this.data.columns[columnIndex] = value;
        this.requestChangeSize({ ids, width: value });
      }
      if (type === "y") {
        this.data.boards = this.data.boards.map((item) => {
          if (item[rowIndex]) {
            ids.push(item[rowIndex].id);
            item[rowIndex].height = value;
          }
          return item;
        });
        this.data.rows[rowIndex] = value;
        this.requestChangeSize({ ids, height: value });
      }
    },
    async requestChangeSize({ ids, width = null, height = null }) {
      try {
        const data = { ids, width, height };
        await this.$http.post("carriage/setup-size", data);
      } catch (e) {
        this.setResponseError({ error: e });
      }
    },
  },
  computed: {
    getLetter: () => getLetter,
    BOX_POSITION: () => BOX_POSITION,
    trueSet: () => trueSet,
    numberOnly: () => numberOnly,
  },
};
</script>
